/**
 * Return-path helpers for the Cognito sign-in round trip.
 *
 * Ported from `frontend/src/utils/return-to.ts`. The v2 version strips a `/v2`
 * prefix because that app is mounted under one; this app is mounted at `/`, so
 * there is nothing to strip.
 */

/**
 * Strips trailing slashes so `/sign-in` and `/sign-in/` compare equal.
 *
 * React Router matches both, so without this an exclusion list keyed on the
 * bare path misses the trailing-slash variant - and `auth/cognito.js`'s
 * `isSignInCallbackPath` already normalises, so the two would disagree about
 * whether the same URL is the callback route.
 */
function normalizePathname(pathname) {
  return pathname.replace(/\/+$/, '') || '/';
}

/**
 * The path to come back to after authenticating, derived from the current URL.
 *
 * @param {string} [href] Defaults to the current location.
 * @param {{ excludePaths?: string[] }} [opts] Paths that should produce no
 *   return target at all - `/sign-out` most importantly, since returning to it
 *   after signing in would immediately sign the user back out, and `/sign-in`,
 *   which would strand the user on a spinner.
 * @returns {string|undefined}
 */
export function computeReturnTo(href, opts) {
  const url = new URL(href || window.location.href);
  const pathname = normalizePathname(url.pathname);

  if (opts && opts.excludePaths && opts.excludePaths.some((p) => normalizePathname(p) === pathname)) {
    return undefined;
  }

  return pathname + url.search + url.hash;
}

/**
 * Guards against open redirects: only same-origin, single-leading-slash
 * relative paths are safe to navigate to or embed in a URL we build.
 *
 * Rejects absolute URLs, protocol-relative `//evil.com`, and backslash
 * variants that some browsers normalise into a scheme separator.
 */
export function isSafeReturnTo(value) {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }

  if (!value.startsWith('/') || value.startsWith('//')) {
    return false;
  }

  if (value.includes('\\')) {
    return false;
  }

  try {
    return new URL(value, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Reads a `?returnTo=` query parameter, returning it only when safe.
 * The legacy app puts one there when redirecting an unauthenticated visitor
 * at `App.jsx`'s catch-all route.
 */
export function readReturnToParam(search = window.location.search) {
  const raw = new URLSearchParams(search).get('returnTo');

  if (!raw) {
    return undefined;
  }

  // The catch-all redirect encodes it, but a hand-built link may not have.
  let candidate = raw;

  try {
    candidate = decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding; fall through and validate the raw value.
  }

  return isSafeReturnTo(candidate) ? candidate : undefined;
}
